import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  JiraIssueRequest,
  JiraCreateIssueResponse,
  JiraPriority
} from '../../models/jira.models';

@Injectable({
  providedIn: 'root'
})
export class JiraService {
  private backendApiUrl: string;

  constructor(private http: HttpClient) {
    this.backendApiUrl = environment.apiUrl;
  }

  /**
   * Crea un issue de tipo Bug en Jira a través del backend
   * @param issueRequest Datos del issue a crear
   * @returns Observable con la respuesta de Jira
   */
  createBugIssue(issueRequest: JiraIssueRequest): Observable<JiraCreateIssueResponse> {
    const url = `${this.backendApiUrl}/jira/create-issue`;

    // Si hay archivos, usar FormData
    if (issueRequest.attachments && issueRequest.attachments.length > 0) {
      const formData = new FormData();
      formData.append('title', issueRequest.title);
      formData.append('description', this.formatDescription(issueRequest));
      formData.append('priority', issueRequest.priority);

      if (issueRequest.reporterName) {
        formData.append('reporterName', issueRequest.reporterName);
      }
      if (issueRequest.reporterEmail) {
        formData.append('reporterEmail', issueRequest.reporterEmail);
      }

      // Agregar archivos
      issueRequest.attachments.forEach((file) => {
        formData.append('attachments', file, file.name);
      });

      return this.http.post<JiraCreateIssueResponse>(url, formData).pipe(
        catchError((error) => {
          console.error('Error creating Jira issue:', error);
          return throwError(() => error);
        })
      );
    } else {
      // Sin archivos, usar JSON
      const payload = {
        title: issueRequest.title,
        description: this.formatDescription(issueRequest),
        priority: issueRequest.priority
        // No enviamos reporterName y reporterEmail al backend porque ya están en la descripción
      };

      return this.http.post<JiraCreateIssueResponse>(url, payload).pipe(
        catchError((error) => {
          console.error('Error creating Jira issue:', error);
          return throwError(() => error);
        })
      );
    }
  }

  /**
   * Formatea la descripción del issue con información adicional
   */
  private formatDescription(issueRequest: JiraIssueRequest): string {
    let description = issueRequest.description;

    if (issueRequest.reporterName || issueRequest.reporterEmail) {
      description += '\n\n---\n*Reportado por:*\n';
      if (issueRequest.reporterName) {
        description += `Nombre: ${issueRequest.reporterName}\n`;
      }
      if (issueRequest.reporterEmail) {
        description += `Email: ${issueRequest.reporterEmail}\n`;
      }
    }

    // Agregar información del navegador y fecha
    description += `\n*Fecha:* ${new Date().toISOString()}`;
    description += `\n*User Agent:* ${navigator.userAgent}`;

    return description;
  }

  /**
   * Obtiene las prioridades disponibles
   */
  getPriorities(): JiraPriority[] {
    return [
      JiraPriority.HIGHEST,
      JiraPriority.HIGH,
      JiraPriority.MEDIUM,
      JiraPriority.LOW,
      JiraPriority.LOWEST
    ];
  }

  /**
   * Obtiene las issues de Jira filtradas por usuario
   * El filtrado se hace en el backend
   * @param userEmail Email del usuario
   * @returns Observable con las issues del usuario
   */
  getUserIssues(userEmail: string): Observable<any> {
    const url = `${this.backendApiUrl}/jira/all-issues?user_email=${encodeURIComponent(userEmail)}`;
    return this.http.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo de issues de un usuario
   * @param userEmail Email del usuario
   * @returns Observable con el conteo de issues
   */
  getUserIssuesCount(userEmail: string): Observable<{ user_email: string; count: number }> {
    const url = `${this.backendApiUrl}/jira/user-issues-count?user_email=${encodeURIComponent(userEmail)}`;

    return this.http.get<{ user_email: string; count: number }>(url).pipe(
      catchError((error) => {
        console.error('Error getting user issues count:', error);
        return throwError(() => error);
      })
    );
  }
}
